import {
    EXCLUDED_ALLOCATION_STATUSES,
    MONTHLY_FULL_LOAD_HOURS,
    NON_WORKING_ALLOCATION_TYPES,
} from '../exportsPage.definitions';
import type {
    AllocationHoursItem,
    DeveloperCapacityItem,
    DeveloperHoursRow,
    MonthKey,
    MonthRange,
} from '../exportsPage.types';
import { calculateAllocationMonthlyHours } from './allocationMonthlyHours';
import { classifyAllocationScope } from './developerCapacity';
import { buildMonthKeyList, toMonthKey } from './monthRange';

/**
 * Meses em que o desenvolvedor tem vínculo, como intervalo `[início, fim]`.
 *
 * Sem data de admissão, considera-se admitido antes da janela. Desativado sem
 * data de demissão não entra em mês nenhum: não há como saber até quando o
 * vínculo valeu.
 */
function resolveEmploymentWindow(
    developer: DeveloperCapacityItem
): { start: MonthKey | null; end: MonthKey | null } | null {
    if (developer.isInactive && !developer.terminationDate) {
        return null;
    }

    return {
        start: developer.admissionDate ? toMonthKey(developer.admissionDate) : null,
        end: developer.terminationDate ? toMonthKey(developer.terminationDate) : null,
    };
}

/** A alocação consome capacidade e cobre ao menos um dia do mês? */
function resolveAllocationWindow(
    allocation: AllocationHoursItem
): { start: MonthKey; end: MonthKey | null } | null {
    if (allocation.allocationType && NON_WORKING_ALLOCATION_TYPES.includes(allocation.allocationType)) {
        return null;
    }

    if (allocation.status && EXCLUDED_ALLOCATION_STATUSES.includes(allocation.status)) {
        return null;
    }

    if (!allocation.startDate || Number.isNaN(allocation.startDate.getTime())) {
        return null;
    }

    const start = toMonthKey(allocation.startDate);
    const end =
        allocation.endDate && !Number.isNaN(allocation.endDate.getTime())
            ? toMonthKey(allocation.endDate)
            : null;

    // Término anterior ao início indica dado inconsistente: nada a contar.
    if (end && end < start) {
        return null;
    }

    return { start, end };
}

/**
 * Monta a planilha de horas dos desenvolvedores — uma linha por mês da janela.
 *
 * Regras:
 * - horas disponíveis = 168h x desenvolvedores com vínculo no mês;
 * - horas alocadas = soma das horas/mês das alocações vigentes no mês, exceto
 *   canceladas, ausências e aviso prévio;
 * - a alocação conta no mês se cobrir ao menos um dia dele, sempre com o valor
 *   mensal cheio (mesmo critério da planilha de alocações por cliente);
 * - Academy e projetos internos são recortes das horas alocadas, não parcelas
 *   somadas a elas;
 * - só entram alocações de quem está na lista de desenvolvedores.
 */
export function buildDeveloperHoursRows(
    developers: DeveloperCapacityItem[],
    allocations: AllocationHoursItem[],
    range: MonthRange
): DeveloperHoursRow[] {
    const months = buildMonthKeyList(range.start, range.end);
    if (months.length === 0) {
        return [];
    }

    const rows = new Map<MonthKey, DeveloperHoursRow>(
        months.map((month) => [
            month,
            {
                month,
                developerCount: 0,
                availableHours: 0,
                allocatedHours: 0,
                academyHours: 0,
                internalProjectHours: 0,
            },
        ])
    );

    const developerIds = new Set(developers.map((developer) => developer.id));

    developers.forEach((developer) => {
        const window = resolveEmploymentWindow(developer);
        if (!window) {
            return;
        }

        months.forEach((month) => {
            if (window.start && month < window.start) {
                return;
            }
            if (window.end && month > window.end) {
                return;
            }

            const row = rows.get(month)!;
            row.developerCount += 1;
            row.availableHours += MONTHLY_FULL_LOAD_HOURS;
        });
    });

    allocations.forEach((allocation) => {
        if (!developerIds.has(allocation.collaboratorId)) {
            return;
        }

        const window = resolveAllocationWindow(allocation);
        if (!window) {
            return;
        }

        const monthlyHours = calculateAllocationMonthlyHours(allocation);
        if (monthlyHours <= 0) {
            return;
        }

        const scope = classifyAllocationScope(allocation.projectName);

        months.forEach((month) => {
            if (month < window.start || (window.end && month > window.end)) {
                return;
            }

            const row = rows.get(month)!;
            row.allocatedHours += monthlyHours;

            if (scope === 'academy') {
                row.academyHours += monthlyHours;
            } else if (scope === 'internal') {
                row.internalProjectHours += monthlyHours;
            }
        });
    });

    return months.map((month) => {
        const row = rows.get(month)!;
        return {
            ...row,
            allocatedHours: round(row.allocatedHours),
            academyHours: round(row.academyHours),
            internalProjectHours: round(row.internalProjectHours),
        };
    });
}

function round(value: number): number {
    return Number.parseFloat(value.toFixed(2));
}
